import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, money, fmtKST, DROP_STATUSES } from "@/lib/api";
import { StatusBadge, FillBar, EmptyState } from "@/components/Bits";
import DropFormDialog from "@/components/admin/DropFormDialog";

const IconBtn = ({ children, ...p }) => (
  <button {...p} className="h-9 w-9 grid place-items-center rounded-lg border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 transition-colors">{children}</button>
);

const STATUS_ORDER = Object.fromEntries(DROP_STATUSES.map(([v], i) => [v, i]));
const SORTS = [
  { key: "store", label: "Store Name", value: (d) => d.store.toLowerCase() },
  { key: "round", label: "Round", value: (d) => d.round_type.toLowerCase() },
  { key: "status", label: "Status", value: (d) => STATUS_ORDER[d.status] ?? 99 },
];

const FILTER_STATUSES = [["open", "Open"], ["collecting_initials", "Collecting Initials"]];

const Chip = ({ active, onClick, testId, children }) => (
  <button type="button" data-testid={testId} aria-pressed={active} onClick={onClick}
    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${active ? "border-violet-400 bg-violet-500/15 text-violet-100" : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"}`}>
    {children}
  </button>
);

export const StatusFilter = ({ picked, setPicked, prefix = "admin-drops" }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-status-filter`}>
    <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mr-1">Show</span>
    <Chip active={!picked.length} onClick={() => setPicked([])} testId={`${prefix}-filter-all`}>All</Chip>
    {FILTER_STATUSES.map(([v, l]) => (
      <Chip key={v} active={picked.includes(v)} testId={`${prefix}-filter-${v}`}
        onClick={() => setPicked((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]))}>{l}</Chip>
    ))}
  </div>
);

const SortBar = ({ sort, onSort }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="admin-drops-sort">
    <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mr-1">Sort by</span>
    {[{ key: null, label: "Newest" }, ...SORTS].map((s) => {
      const active = sort.key === s.key;
      const Icon = sort.dir === 1 ? ArrowUp : ArrowDown;
      return (
        <button key={s.label} type="button" data-testid={`admin-drops-sort-${s.key || "newest"}`} aria-pressed={active} onClick={() => onSort(s.key)}
          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${active ? "border-violet-400 bg-violet-500/15 text-violet-100" : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"}`}>
          {s.label} {active && s.key && <Icon className="h-3 w-3" />}
        </button>
      );
    })}
  </div>
);

export default function DropsAdmin({ refreshKey = 0 }) {
  const [drops, setDrops] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [picked, setPicked] = useState([]);
  const onSort = (key) => setSort((s) => ({ key, dir: key && s.key === key ? -s.dir : 1 }));
  const sorted = useMemo(() => {
    const col = SORTS.find((s) => s.key === sort.key);
    const shown = picked.length ? drops.filter((d) => picked.includes(d.status)) : drops;
    if (!col) return shown;
    return [...shown].sort((a, b) => {
      const x = col.value(a), y = col.value(b);
      return ((x < y ? -1 : x > y ? 1 : 0) || a.store.localeCompare(b.store)) * sort.dir;
    });
  }, [drops, sort, picked]);
  const load = useCallback(() => {
    api.get("/drops").then((r) => setDrops(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const setStatus = async (d, status) => {
    try {
      const { id, filled_slots, total_slots, created_at, ...rest } = d;
      await api.put(`/drops/${id}`, { ...rest, status });
      toast.success(`Status: ${Object.fromEntries(DROP_STATUSES)[status]}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}" and all ${d.filled_slots} claims? This can't be undone.`)) return;
    try {
      await api.delete(`/drops/${d.id}`);
      toast.success("Drop deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button data-testid="admin-new-drop-button" onClick={() => setEditing({})} className="rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 text-white font-semibold hover:opacity-90">
          <Plus className="h-4 w-4 mr-1.5" /> New drop
        </Button>
        {drops.length > 1 && <SortBar sort={sort} onSort={onSort} />}
      </div>
      {drops.length > 0 && <div className="mt-4"><StatusFilter picked={picked} setPicked={setPicked} /></div>}
      <div className="mt-5 space-y-3" data-testid="admin-drops-list">
        {drops.length === 0 && <EmptyState title="No drops yet" hint="Create your first drop to open it to joiners." />}
        {drops.length > 0 && sorted.length === 0 && <EmptyState testId="admin-drops-filter-empty" title="No drops with these statuses" />}
        {sorted.map((d) => (
          <div key={d.id} data-testid={`admin-drop-${d.id}`} className="grid md:grid-cols-[1fr_220px_auto] gap-4 items-center rounded-2xl border border-white/5 bg-zinc-900/60 p-4 sm:p-5">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2"><StatusBadge status={d.claims_closed ? "closed" : d.status} label={d.claims_closed ? "Claims closed" : undefined} />{d.scheduled && <span data-testid={`admin-drop-scheduled-${d.id}`} className="text-[11px] rounded-full bg-sky-500/15 text-sky-200 px-2 py-0.5 font-semibold">Hidden · opens {fmtKST(d.opens_at)}</span>}<span className="text-xs text-zinc-500 truncate">{d.store}</span></div>
              <div className="font-display font-bold text-lg mt-1.5 truncate">{d.title}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{d.num_sets} sets · {d.members.length} members · {money(d.price_per_member)} each</div>
            </div>
            <FillBar filled={d.filled_slots} total={d.total_slots} />
            <div className="flex gap-2">
              <Link to={`/drops/${d.id}`} data-testid={`admin-view-drop-${d.id}`} className="h-9 w-9 grid place-items-center rounded-lg border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 transition-colors"><ExternalLink className="h-4 w-4" /></Link>
              <IconBtn data-testid={`admin-edit-drop-${d.id}`} onClick={() => setEditing(d)} aria-label="Edit"><Pencil className="h-4 w-4" /></IconBtn>
              <Select value={d.status} onValueChange={(v) => setStatus(d, v)}>
                <SelectTrigger data-testid={`admin-status-drop-${d.id}`} className="h-9 w-44 bg-zinc-950 border-zinc-800 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{DROP_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <IconBtn data-testid={`admin-delete-drop-${d.id}`} onClick={() => remove(d)} aria-label="Delete"><Trash2 className="h-4 w-4" /></IconBtn>
            </div>
          </div>
        ))}
      </div>
      {editing && <DropFormDialog drop={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
